'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import type { HomepagePcBuilderSideBanners } from '@/lib/homepage-config';

export default function GlobalSideBanners({
  sideBanners,
}: {
  sideBanners: HomepagePcBuilderSideBanners;
}) {
  const pathname = usePathname();
  const shouldHideByContext = !sideBanners?.enabled || pathname?.startsWith('/admin');

  const width = sideBanners.width || 136;
  const height = sideBanners.height || 339;
  const leftOffsetX = Number(sideBanners.left?.offsetX || 0);
  const leftOffsetY = Number(sideBanners.left?.offsetY || 0);
  const rightOffsetX = Number(sideBanners.right?.offsetX || 0);
  const rightOffsetY = Number(sideBanners.right?.offsetY || 0);
  const [viewportWidth, setViewportWidth] = useState(1920);
  const contentMaxWidth = 1500;
  const safeGap = 12;

  useEffect(() => {
    const updateViewport = () => {
      setViewportWidth(window.innerWidth || 1920);
    };
    updateViewport();

    let rafId = 0;
    const onResize = () => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateViewport);
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const { leftBaseX, leftTop, rightBaseX, rightTop, showBanners, showLeftBanner, showRightBanner } =
    useMemo(() => {
      const show = viewportWidth >= 1100;
      const contentWidth = Math.min(contentMaxWidth, Math.max(viewportWidth - 32, 0));
      const contentLeft = Math.max((viewportWidth - contentWidth) / 2, 0);
      const leftPanelRight = leftOffsetX + width;
      const rightPanelLeft = viewportWidth - rightOffsetX - width;

      const showLeft =
        show &&
        Boolean(sideBanners.left?.enabled && sideBanners.left?.img) &&
        leftPanelRight + safeGap <= contentLeft;
      const showRight =
        show &&
        Boolean(sideBanners.right?.enabled && sideBanners.right?.img) &&
        rightPanelLeft >= contentLeft + contentWidth + safeGap;

      return {
        leftBaseX: `calc(50% - ${680 + Number(width) - Number(leftOffsetX)}px)`,
        leftTop: leftOffsetY,
        rightBaseX: `calc(50% - ${680 + Number(width) - Number(rightOffsetX)}px)`,
        rightTop: rightOffsetY,
        showBanners: show,
        showLeftBanner: showLeft,
        showRightBanner: showRight,
      };
    }, [
      leftOffsetX,
      rightOffsetX,
      viewportWidth,
      width,
      sideBanners.left?.enabled,
      sideBanners.left?.img,
      sideBanners.right?.enabled,
      sideBanners.right?.img,
      leftOffsetY,
      rightOffsetY,
      width,
    ]);

  if (shouldHideByContext || !showBanners) return null;

  return (
    <>
      {sideBanners.left?.enabled && sideBanners.left?.img && showLeftBanner ? (
        <div
          className="pointer-events-none fixed z-[62]"
          style={{
            left: leftBaseX,
            top: `${leftTop}px`,
            transition: 'left 220ms ease, top 220ms ease, right 220ms ease',
          }}
        >
          <div className="floating-banner-motion floating-banner-motion--left">
            <Link
              href={sideBanners.left.href || '#'}
              className="pointer-events-auto block overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-[0_16px_34px_rgba(15,23,42,0.18)]"
              target={sideBanners.left.href?.startsWith('http') ? '_blank' : undefined}
              rel={sideBanners.left.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              style={{ width: `${width}px`, height: `${height}px` }}
            >
              <Image
                src={sideBanners.left.img}
                alt={sideBanners.left.alt || 'Banner trái'}
                width={width}
                height={height}
                unoptimized
                className="block object-contain"
                style={{
                  width: `${width}px`,
                  height: `${height}px`,
                  objectPosition: 'center center',
                }}
              />
            </Link>
          </div>
        </div>
      ) : null}

      {sideBanners.right?.enabled && sideBanners.right?.img && showRightBanner ? (
        <div
          className="pointer-events-none fixed z-[62]"
          style={{
            right: rightBaseX,
            top: `${rightTop}px`,
            transition: 'left 220ms ease, top 220ms ease, right 220ms ease',
          }}
        >
          <div className="floating-banner-motion floating-banner-motion--right">
            <Link
              href={sideBanners.right.href || '#'}
              className="pointer-events-auto block overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-[0_16px_34px_rgba(15,23,42,0.18)]"
              target={sideBanners.right.href?.startsWith('http') ? '_blank' : undefined}
              rel={sideBanners.right.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              style={{ width: `${width}px`, height: `${height}px` }}
            >
              <Image
                src={sideBanners.right.img}
                alt={sideBanners.right.alt || 'Banner phải'}
                width={width}
                height={height}
                unoptimized
                className="block object-contain"
                style={{
                  width: `${width}px`,
                  height: `${height}px`,
                  objectPosition: 'center center',
                }}
              />
            </Link>
          </div>
        </div>
      ) : null}
    </>
  );
}
